/**
 * Collector Reconciliation Types
 * Conciliación CC ↔ CR (CreditContract vs CollectionRecord)
 * El CC es el registro contable; el CR es el registro operativo. Ambos deben existir y cuadrar.
 */

// ─── Conteos por status ─────────────────────────────────────────────────────

export interface ReconciliationCounts {
  ok: number;
  soloCC: number;
  soloCR: number;
  descuadre: number;
}

/**
 * CollectionRecord individual dentro del array `records[]` de un CC.
 * Shape definido en credit-worker `reconciliation.types.ts` (ReconciliationCRDetail).
 */
export interface ReconciliationCRDetail {
  credit_id: string;
  coupon_number: number;
  payment_type?: string; // REGULAR | ADVANCED | ANTICIPATED | SETTLEMENT
  amount_collected_mxn: number;
  collection_status: string; // FULL | PARTIAL | NONE
  record_id?: string;
  reference?: string;
  collection_date?: string;
}

/** Generic detail row — the API returns varied shapes per status */
export type DetailRow = Record<string, unknown>;

export interface ReconciliationMonth {
  ok: number;
  soloCC: number;
  soloCR: number;
  descuadre: number;
  detalles: {
    ok: DetailRow[];
    soloCC: DetailRow[];
    soloCR: DetailRow[];
    descuadre: DetailRow[];
  };
}

// ─── Response completa ──────────────────────────────────────────────────────

export interface CollectorReconciliationResumen {
  year: number;
  totalCredits: number;
  totalCollectionRecords: number;
  totalCreditContracts: number;
  timings: Record<string, number>;
  totals: ReconciliationCounts;
}

export interface CollectorReconciliationData {
  resumen: CollectorReconciliationResumen;
  meses: Record<string, ReconciliationMonth>;
}

// ─── Registro de conciliacion ───────────────────────────────────────────────

export interface RegisterReconciliationRequest {
  year: number;
  month: number | null;
  totals: ReconciliationCounts;
  status: 'LIMPIO' | 'CON_DISCREPANCIAS';
  notes?: string;
}

export interface ReconciliationRecord {
  reconciliation_id: string;
  year: number;
  month: number | null;
  executed_at: string;
  executed_by: string;
  totals: Record<string, unknown>;
  status: 'LIMPIO' | 'CON_DISCREPANCIAS';
  notes?: string;
}

// ─── UI state ───────────────────────────────────────────────────────────────

export interface DrillDownSelection {
  month: number;
  statusKey: string;
}

/** Rich tooltip for column headers */
export interface ColumnTooltip {
  title: string;
  description: string;
  relation?: string;
  conditions?: { label: string; items: string[]; severity: 'ok' | 'error' }[];
  note?: string;
}

/** Column config for the summary table */
export interface SummaryColumn {
  key: string;
  label: string;
  tooltip: ColumnTooltip;
  color: 'green' | 'red' | 'amber' | 'rose' | 'slate';
}

export const SUMMARY_COLUMNS: SummaryColumn[] = [
  {
    key: 'ok', label: 'OK', color: 'green',
    tooltip: {
      title: 'CC con respaldo operativo completo',
      description: 'El CreditContract tiene CollectionRecord(s) y la suma de amount_collected_mxn cuadra con creditNotional (tolerancia ±$1.00 MXN).',
      relation: 'CollectionRecords_GT.credit_contract_id → CreditContract.id (FK directo)',
      note: 'Cardinalidad CC→CR: REGULAR (1→1), ADVANCED (1→N), ANTICIPATED (1→1), SETTLEMENT (1→N).',
    },
  },
  {
    key: 'soloCC', label: 'Solo CC', color: 'red',
    tooltip: {
      title: 'Contrato sin respaldo operativo',
      description: 'CreditContract existe pero ningun CollectionRecord tiene su id como credit_contract_id.',
      conditions: [
        { label: 'SIEMPRE ES ERROR', items: ['Registro contable sin respaldo operativo', 'Investigar de inmediato'], severity: 'error' },
      ],
      note: 'El CC fue creado por el collector pero credit-worker no genero el CR correspondiente.',
    },
  },
  {
    key: 'soloCR', label: 'Solo CR', color: 'rose',
    tooltip: {
      title: 'Registro operativo sin contrato',
      description: 'CollectionRecord tiene un credit_contract_id que no existe en la tabla CreditContract.',
      conditions: [
        { label: 'SIEMPRE ES ERROR', items: ['El CR apunta a un CC inexistente (FK huerfano)', 'Verificar si el CC fue eliminado o nunca se creo'], severity: 'error' },
      ],
    },
  },
  {
    key: 'descuadre', label: 'Descuadre', color: 'amber',
    tooltip: {
      title: 'Montos no cuadran entre CC y CRs',
      description: 'El CreditContract tiene CollectionRecord(s), pero la suma de amount_collected_mxn NO cuadra con creditNotional (fuera de tolerancia ±$1.00 MXN). Puede ser de mas o de menos.',
      conditions: [
        { label: 'Posibles causas', items: ['Cobro pendiente de completar', 'Diferencia de redondeo FX fuera de tolerancia', 'CRs faltantes o duplicados'], severity: 'error' },
      ],
    },
  },
];

export const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ─── Entity config (for comparison modal) ───────────────────────────────────

export interface TabEntityConfig {
  name: string;
  color: string;
  fields: { key: string; label: string; format?: 'date' | 'currency' | 'currency_usd' | 'mono' }[];
}

export interface ReconciliationConfig {
  label: string;
  description: string;
  color: string;
  entityA: TabEntityConfig;
  entityB: TabEntityConfig;
  /** Status keys that represent a successful match (show 2 panels) */
  matchStatuses: string[];
}

export const RECONCILIATION_CONFIG: ReconciliationConfig = {
  label: 'CC ↔ CR',
  description: 'Cruza CreditContract (contable) con CollectionRecord (operativo)',
  color: 'blue',
  entityA: {
    name: 'CreditContract',
    color: 'purple',
    fields: [
      { key: 'ccId', label: 'CC ID', format: 'mono' },
      { key: 'ccStatus', label: 'Status' },
      { key: 'ccCreditNotional', label: 'Notional', format: 'currency' },
      { key: 'ccCreditTotal', label: 'Total', format: 'currency' },
      { key: 'ccCreditFee', label: 'Fee', format: 'currency' },
      { key: 'ccCreditTax', label: 'Tax (IVA)', format: 'currency' },
      { key: 'ccCreateDate', label: 'Fecha CC', format: 'date' },
      { key: 'ccPaymentDate', label: 'Fecha Pago', format: 'date' },
      { key: 'ccRelatedTransaction', label: 'Transaccion', format: 'mono' },
      { key: 'ccDirectoryId', label: 'Directory ID', format: 'mono' },
      { key: 'ccTenantId', label: 'Tenant' },
      { key: 'ccCollectionType', label: 'Tipo Cobro' },
      { key: 'ccProvider', label: 'Proveedor' },
    ],
  },
  entityB: {
    name: 'CollectionRecords',
    color: 'teal',
    fields: [
      { key: 'cantidadCRs', label: '# Records' },
      { key: 'sumaColectadaCR', label: 'Suma Colectada', format: 'currency' },
      { key: 'diferencia', label: 'Diferencia', format: 'currency' },
    ],
  },
  matchStatuses: ['ok', 'descuadre'],
};
